import { useCallback } from 'preact/hooks'
import { Signal, signal, useSignalEffect } from '@preact/signals'
import { MarkupText, Text } from 'preact-i18n'
import { Icon, Menu, MenuItemProps } from 'semantic-ui-react'

import { useStore, useTracker } from '../../../store'
import { ScannerMode } from '../../../store/InitialOptionsStore'
import { MenuSettingName } from '../../../store/SettingsStore'
import { SymbologyOption, SymbologyOptionExtension } from '../../../store/SymbologyOption'
import { MainMenuItemContainer } from '../../atoms'
import { Show } from '../../directives'
import AdaptiveRecognitionGateModal from '../AdaptiveRecognitionGateModal'
import BarcodeMenuOptionOverlay from './BarcodeMenuOptionOverlay'
import CameraDropDown from './CameraDropDown'
import LabelMenuOptionOverlay from './LabelMenuOptionOverlay'
import styles from './MainMenu.module.css'

const areGateOpen = signal(false)

interface MainMenuProps {
  activeSubMenu: Signal<string>
}

const MainMenu = ({ activeSubMenu }: MainMenuProps) => {
  const store = useStore()
  const tracker = useTracker()

  const scannerMode = store.initialOptionsStore.scannerMode.value
  const isBatchMode = scannerMode === ScannerMode.Batch
  const isLabelMode = scannerMode === ScannerMode.LabelScanning
  const showExtraOptions = !isBatchMode && !isLabelMode

  const toggleSubmenu = useCallback(
    (_e: MouseEvent, data: MenuItemProps) => {
      if (data.name !== 'settings') return
      const willClose = activeSubMenu.value === data.name
      if (willClose && store.labelCaptureSettingsStore?.requiresAdaptiveRecognitionEnabled()) {
        areGateOpen.value = true
        return
      }
      activeSubMenu.value = willClose ? '' : (data.name ?? '')
    },
    [activeSubMenu, store]
  )

  const handleAreGateEnable = useCallback(() => {
    store.labelCaptureSettingsStore?.setAdaptiveRecognition(true)
    areGateOpen.value = false
    activeSubMenu.value = ''
  }, [store, activeSubMenu])

  const handleAreGateCancel = useCallback(() => {
    store.labelCaptureSettingsStore?.revertPresetToSnapshot()
    areGateOpen.value = false
    activeSubMenu.value = ''
  }, [store, activeSubMenu])

  const handleMenuSettingClick = useCallback(
    (_e: MouseEvent, { name }: MenuItemProps) => {
      const actions: Record<MenuSettingName, () => void> = {
        duplicateFilter: async () => {
          tracker.sendEvent('Setting', 'duplicate-filter')
          await store.settingsStore.setDuplicateFilter(!store.settingsStore.duplicateFilter.value).apply()
        },
        soundEffects: async () => {
          tracker.sendEvent('Setting', 'sound')
          const settingsStore = await store.settingsStore.setSoundEffects(!store.settingsStore.soundEffects.value)
          await settingsStore.apply()
        },
      }
      actions[name as MenuSettingName]?.()
    },
    [store, tracker]
  )

  const handlePresetChange = useCallback(
    (value: string) => {
      tracker.sendEvent('Setting', 'label-preset')
      store.labelCaptureSettingsStore?.setPreset(value as any)
    },
    [tracker, store]
  )

  const handleDateFormatChange = useCallback(
    (value: string) => {
      tracker.sendEvent('Setting', 'label-date-format')
      store.listStore?.setDateFormat(value as any)
      store.labelCaptureSettingsStore?.setDateFormat(value as any)
    },
    [tracker, store]
  )

  const handleAdaptiveRecognitionChange = useCallback(
    (value: boolean) => {
      tracker.sendEvent('Setting', 'label-adaptive-recognition')
      store.labelCaptureSettingsStore?.setAdaptiveRecognition(value)
    },
    [tracker, store]
  )

  const handleAcceptPartialDatesChange = useCallback(
    (value: boolean) => {
      tracker.sendEvent('Setting', 'label-accept-partial-dates')
      store.labelCaptureSettingsStore?.setAcceptPartialDates(value)
    },
    [tracker, store]
  )

  const handlePricesIncludeDecimalsChange = useCallback(
    (value: boolean) => {
      tracker.sendEvent('Setting', 'label-prices-include-decimals')
      store.labelCaptureSettingsStore?.setPricesIncludeDecimals(value)
    },
    [tracker, store]
  )

  useSignalEffect(() => {
    const isSettingsOpen = activeSubMenu.value === 'settings'
    const labelStore = store.labelCaptureSettingsStore
    if (!isLabelMode || !labelStore) return
    if (isSettingsOpen) {
      void labelStore.beginEditing()
    } else {
      void labelStore.endEditing()
    }
  })

  const handleToggleSymbology = useCallback(
    (symbologyOption: SymbologyOption) => {
      tracker.sendEvent('Setting', 'barcode-symbology')
      return store.settingsStore.toggleSymbology(symbologyOption.symbology).apply()
    },
    [tracker, store]
  )

  const handleToggleAddon = useCallback(
    (option: SymbologyOptionExtension) => {
      tracker.sendEvent('Setting', 'barcode-symbology-addon')
      return store.settingsStore.setExtensions([option.extension.name], !option.checked.peek()).apply()
    },
    [tracker, store]
  )

  return (
    <div class={styles.container}>
      <Menu
        id="options-menu"
        inverted
        widths={showExtraOptions ? 4 : 2}
        fluid
        compact
        icon="labeled"
        pointing
        size="small"
        onDoubleClick={(e: MouseEvent) => e.stopPropagation()}
      >
        <Menu.Item id="settings" name="settings" onClick={toggleSubmenu} active={activeSubMenu.value === 'settings'}>
          <MainMenuItemContainer active>
            <Icon name={showExtraOptions ? 'barcode' : 'setting'} />
            <div>
              <Text id="options.barcodes" />
            </div>
            <Icon name="dropdown" />
          </MainMenuItemContainer>
        </Menu.Item>

        <CameraDropDown />

        <Show when={showExtraOptions}>
          <Menu.Item id="soundEffects" name="soundEffects" onClick={handleMenuSettingClick}>
            <MainMenuItemContainer active={store.settingsStore?.soundEffects.value ?? false}>
              <Icon name="music" />
              <MarkupText id="options.soundEffects" />
            </MainMenuItemContainer>
          </Menu.Item>
          <Menu.Item id="duplicateFilter" name="duplicateFilter" onClick={handleMenuSettingClick}>
            <MainMenuItemContainer active={store.settingsStore?.duplicateFilter.value ?? false}>
              <Icon name="history" />
              <MarkupText id="options.duplicateFilter" />
            </MainMenuItemContainer>
          </Menu.Item>
        </Show>
      </Menu>

      {isLabelMode && store.labelCaptureSettingsStore != null && (
        <LabelMenuOptionOverlay
          open={activeSubMenu.value === 'settings'}
          selectedPreset={store.labelCaptureSettingsStore.selectedPreset}
          selectedDateFormat={store.labelCaptureSettingsStore.selectedDateFormat}
          adaptiveRecognition={store.labelCaptureSettingsStore.adaptiveRecognition}
          acceptPartialDates={store.labelCaptureSettingsStore.acceptPartialDates}
          pricesIncludeDecimals={store.labelCaptureSettingsStore.pricesIncludeDecimals}
          onPresetChange={handlePresetChange}
          onDateFormatChange={handleDateFormatChange}
          onAdaptiveRecognitionChange={handleAdaptiveRecognitionChange}
          onAcceptPartialDatesChange={handleAcceptPartialDatesChange}
          onPricesIncludeDecimalsChange={handlePricesIncludeDecimalsChange}
        />
      )}

      <Show when={!isLabelMode && store.settingsStore != null}>
        <BarcodeMenuOptionOverlay
          open={activeSubMenu.value === 'settings'}
          symbologiesOptions={store.settingsStore?.symbologiesOptions ?? []}
          symbologiesOptionsAddon={store.settingsStore?.symbologiesAddonsOptions}
          toggleSymbology={handleToggleSymbology}
          toggleAddon={handleToggleAddon}
        />
      </Show>

      <AdaptiveRecognitionGateModal
        open={areGateOpen.value}
        onEnable={handleAreGateEnable}
        onCancel={handleAreGateCancel}
      />
    </div>
  )
}

export default MainMenu
